// SSOT for every orientation-dependent recharts decision in a
// `(cartesian …)` tile. The category↔value axis identity, the recharts
// `layout`, the grid flags, the reference-marker coordinate mapping and
// the Tooltip target axis must all agree; deriving them independently
// per component would be the cross-file consistency hazard the repo's
// SSOT rule forbids. One pure model, built once in `CartesianChart`
// and threaded to each wrapper as the `o` prop — wrapper components ask
// it *questions* (semantic methods), they never branch on raw
// orientation flags.
//
// Recharts v3 contract (verified against the v3 Tooltip API + 3.0
// migration guide): the Tooltip follows the *index* axis — X for
// `layout="horizontal"`, Y for `layout="vertical"` — and with multiple
// axes you MUST pass `axisId` or it falls back to an arbitrary "first"
// axis. The previous code only gave the category axis an explicit id in
// one orientation, so the flipped layout's Tooltip/cursor bound to the
// wrong axis. Here the category axis carries the same stable id in
// *both* orientations and `tooltipAxisId` points at it.

import type { CartesianSpec } from '#core/lib/expr/ops/dashboard/value.ts';
import { toDateValue } from '#core/lib/expr/ops/transform/date-utils.ts';

import { AXIS_TITLE_RESERVE } from './axisTitleStyle.ts';

/** Stable recharts id for the shared category/index axis (both orientations). */
export const CATEGORY_AXIS = 'category';

type ValueSide = 'left' | 'right';
type PointCoord = { xAxisId: string; yAxisId: string } & ({ x: number | string } | { y: number | string });
type RangeCoord = { xAxisId: string; yAxisId: string } & (
  | { x1: number | string; x2: number | string }
  | { y1: number | string; y2: number | string }
);

export interface ChartOrientation {
  /** recharts `<ComposedChart layout>`. */
  readonly rechartsLayout: 'horizontal' | 'vertical';
  /** Axis the `<Tooltip>` must follow — always the category/index axis. */
  readonly tooltipAxisId: string;
  /** Stable id carried by the category axis in both orientations. */
  readonly categoryAxisId: string;
  /** Category axis is a continuous (number) axis — time/numeric x-kind. */
  readonly categoryNumeric: boolean;
  /**
   * True when the category axis is the bottom (horizontal-layout) X
   * axis; false when it is a side (Y) axis. Discriminates which literal
   * recharts element each axis component renders.
   */
  readonly categoryIsBottom: boolean;
  /** Both axis ids a series/band bound to `side` must carry. */
  seriesAxisIds(side: ValueSide): { readonly xAxisId: string; readonly yAxisId: string };
  /**
   * Horizontal margin (px) the chart reserves on each physical side for a
   * rotated axis title, given which logical titles are present. Rotated
   * titles only occur on the side (Y) axes: the value axes when the
   * category axis is on the bottom, otherwise the category axis (left).
   * Bottom titles need no horizontal reserve — their axis height holds them.
   */
  marginReserve(present: { valueLeft: boolean; valueRight: boolean; category: boolean }): {
    readonly left: number;
    readonly right: number;
  };
  /** `<CartesianGrid>` orientation flags + the axes it scales against. */
  gridProps(): {
    readonly horizontal: boolean;
    readonly vertical: boolean;
    readonly xAxisId: string;
    readonly yAxisId: string;
  };
  /**
   * `<ReferenceLine>` coords for a marker anchored to a *logical* axis
   * (`'x'` = category, `'y'` = value/left).
   */
  refLineCoords(logical: 'x' | 'y', value: number | string): PointCoord;
  /** `<ReferenceArea>` coords (range) for the same logical anchoring. */
  refAreaCoords(logical: 'x' | 'y', start: number | string, end: number | string): RangeCoord;
  /** Normalize a reference-marker value to what the bound axis expects:
   * epoch-ms on a time x-axis, the raw value (string or number) on a discrete
   * category x-axis, otherwise a number. */
  normalizeRef(axis: 'x' | 'y', value: number | string): number | string;
}

/** Pure orientation model. Unit-tested directly; the app reads it via the `o` prop. */
export function chartOrientationModel(spec: CartesianSpec): ChartOrientation {
  // DSL `vertical` (vertical bars / time series) ⇒ recharts
  // `layout="horizontal"` (category on X). DSL `horizontal`
  // (horizontal bars) ⇒ recharts `layout="vertical"` (category on Y).
  // Decide *once* which physical axis letter carries each logical role;
  // every other orientation-dependent answer is derived from this pair
  // instead of re-branching on the raw flag.
  const horizontal = spec.orientation === 'horizontal';
  const catLetter: 'x' | 'y' = horizontal ? 'y' : 'x';
  const valLetter: 'x' | 'y' = horizontal ? 'x' : 'y';
  // Resolved discrete-vs-continuous decision for the category axis.
  // Continuous (numeric/time) gives recharts a `type="number"` axis,
  // which picks "nice" ticks on the domain — fine for line/area, but
  // misaligned with bars (bar positions follow the data, ticks don't).
  // Flipping to discrete = `type="category"` gives one equal-width slot
  // per row so bars align exactly to ticks. The `:spacing` kwarg
  // overrides; `'auto'` (the default when undefined) resolves to
  // discrete iff any series is a bar.
  const isTime = spec.xKind === 'time';
  const isContinuousKind = spec.xKind === 'time' || spec.xKind === 'numeric';
  const hasBar = spec.series.some((s) => s.type === 'bar');
  const spacing = spec.xSpacing ?? 'auto';
  const wantsDiscrete = spacing === 'discrete' || (spacing === 'auto' && hasBar);
  const categoryNumeric = isContinuousKind && !wantsDiscrete;

  // Spreadable `{ xAxisId }` / `{ yAxisId }` for a physical axis letter.
  const axisId = (letter: 'x' | 'y', id: string): { xAxisId: string } | { yAxisId: string } =>
    letter === 'x' ? { xAxisId: id } : { yAxisId: id };
  // Single source for both reference-marker shapes: a point (`b`
  // omitted) or a range (`b` given), on whichever physical axis the
  // logical role maps to. `logical 'y'` = value axis (left); `'x'` =
  // category axis.
  const markerCoords = (logical: 'x' | 'y', a: number | string, b?: number | string): PointCoord | RangeCoord => {
    const letter = logical === 'y' ? valLetter : catLetter;
    const ids = { ...axisId(catLetter, CATEGORY_AXIS), ...axisId(valLetter, 'left') } as {
      xAxisId: string;
      yAxisId: string;
    };
    if (b === undefined) {
      return letter === 'x' ? { ...ids, x: a } : { ...ids, y: a };
    }
    return letter === 'x' ? { ...ids, x1: a, x2: b } : { ...ids, y1: a, y2: b };
  };

  return {
    rechartsLayout: horizontal ? 'vertical' : 'horizontal',
    tooltipAxisId: CATEGORY_AXIS,
    categoryAxisId: CATEGORY_AXIS,
    categoryNumeric,
    categoryIsBottom: !horizontal,
    seriesAxisIds: (side) =>
      ({ ...axisId(catLetter, CATEGORY_AXIS), ...axisId(valLetter, side) }) as {
        xAxisId: string;
        yAxisId: string;
      },
    // Horizontal layout ⇒ category is the rotated (left) axis and the value
    // axes sit on the bottom; vertical layout ⇒ the value axes are the
    // rotated left/right titles. Only rotated titles need a horizontal strip.
    marginReserve: ({ valueLeft, valueRight, category }) =>
      horizontal
        ? { left: category ? AXIS_TITLE_RESERVE : 0, right: 0 }
        : {
            left: valueLeft ? AXIS_TITLE_RESERVE : 0,
            right: valueRight ? AXIS_TITLE_RESERVE : 0,
          },
    gridProps: () =>
      ({
        horizontal: !horizontal,
        vertical: horizontal,
        // Scale against the category axis + the always-present left value axis.
        ...axisId(catLetter, CATEGORY_AXIS),
        ...axisId(valLetter, 'left'),
      }) as { horizontal: boolean; vertical: boolean; xAxisId: string; yAxisId: string },
    refLineCoords: (logical, value) => markerCoords(logical, value) as PointCoord,
    refAreaCoords: (logical, start, end) => markerCoords(logical, start, end) as RangeCoord,
    normalizeRef: (axis, value) => {
      if (axis === 'x' && isTime) {
        return toDateValue(value)?.getTime() ?? Number.NaN;
      }
      if (axis === 'x' && !categoryNumeric) {
        return value;
      }
      return typeof value === 'number' ? value : Number(value);
    },
  };
}
